//@flow
import React, { PureComponent } from 'react';
import { themr } from 'react-css-themr';
import { connect } from 'react-redux';
import { Checkbox } from 'common/components/core/checkbox';
import classnames from 'classnames';
import { translate } from 'react-i18next';
import compose from 'recompose/compose';
import TransporterActions from 'common/redux/actions/transporter';
import AppPreferencesActions from 'common/redux/actions/appPreferences';
import TransporterMasterImportContainer from 'common/entity/transporterMaster/TransporterMasterImportContainer';
import SkillActions from 'common/redux/actions/skill';
import SkillSelectors from 'common/redux/selectors/skill';
import ToastActions from 'common/redux/actions/toast';

import {
    clientSelector,
    isAddEntityActive as isAddEntityActiveSelector,
    isImportEntityActive as isImportEntityActiveSelector,
    isExportEntityActive as isExportEntityActiveSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';
import { MasterExportContainerWithDocuments } from 'common/entity/documents';
import TransporterSelectors from 'common/redux/selectors/transporter';
import { getEntitySettings } from 'common/redux/selectors/clientSelector';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';
import { teamsSelector } from 'common/redux/selectors/teamSelector';

import { withRouter } from 'common/components/core/router';

import SingleTransporterEditContainer from './SingleTransporterEditContainer';

import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import GeneralFilter from 'common/entity/components/GeneralFilter';
import { ImageWithTooltip } from 'common/components/core/image/Image';

import theme from 'common/entity/BulkEditContainer.scss';

import { dashboardColors } from 'common/enums/ColorEnum';
import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';
import {
    ConnectedTeamSelectorFilter,
    ConnectedTeamIdSelectorFilter
} from 'common/entity/components/TeamSelectorFilter';
import TeamList from 'common/components/core/common/TeamList';

import type { MasterTransporter } from 'common/models/internal/MasterTransporter';
import { TransporterTypeType } from 'common/models/internal/MasterTransporter';

import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { TFunction } from 'react-i18next';
import type { Skill } from 'common/models/internal/Skill';

import { CellDataTypes } from 'common/components/core/table';
import VisitUtils from 'common/utils/VisitUtils';

import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import {
    ENTITY_NAME_ID,
    ENTITY_EXPORT_ID,
    ENTITY_TABLE_ID
} from 'common/constants/entity/transporterMaster';
import { RoleOperationEnum } from 'common/modules/rbac';

const selectTeamId = ({ teamId }) => teamId;

const getTeamIdFilter = ({ clientId, filteredColumn }) => {
    const {
        value: { values, allowEmptyOrNull }
    } = filteredColumn;
    const teamValueFilter = values.map(team => ({
        clientId,
        teamId: team
    }));

    return new FilterBuilder()
        .withName('teams')
        .withOperation(FilterOperation.Contains)
        .withValues(teamValueFilter)
        .withAllowEmptyOrNull(allowEmptyOrNull)
        .withCaseSensitive(true);
};

const DEFAULT_COLUMNS = [
    {
        id: 'selector',
        Cell: props => {
            const {
                tdProps: {
                    rest: { isChecked, onCheckboxClick }
                }
            } = props;
            return (
                <Checkbox
                    checkboxColor={dashboardColors.darkSkyBlue.name}
                    className={theme.selectionCell}
                    theme={theme}
                    checked={isChecked}
                    onChange={onCheckboxClick}
                />
            );
        },
        sortable: false,
        filterable: false,
        width: 40
    },
    {
        headerTextId: 'transporterTable.id',
        accessor: 'id',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'transporterTable.code',
        accessor: 'code',
        infixSearchable: true,
        show: false
    },
    {
        headerTextId: 'transporterTable.isActive',
        accessor: 'isActive',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const { isActive } = original;
            return isActive
                ? t('transporterTable.boolean.yes')
                : t('transporterTable.boolean.no');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            const optionsForSelect = [
                { value: true, label: t('transporterTable.boolean.yes') },
                { value: false, label: t('transporterTable.boolean.no') }
            ];
            return optionsForSelect;
        }
    },
    {
        headerTextId: 'transporterTable.name',
        accessor: 'name'
    },
    {
        headerTextId: 'transporterTable.registeredName',
        accessor: 'registeredName'
    },
    {
        headerTextId: 'transporterTable.tagline',
        accessor: 'tagline',
        minWidth: 100
    },
    {
        headerTextId: 'transporterTable.address.placeName',
        accessor: 'address.placeName',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'transporterTable.address.address',
        accessor: 'address.formattedAddress',
        minWidth: 300,
        infixSearchable: true
    },
    {
        headerTextId: 'transporterTable.address.locality',
        accessor: 'address.localityName',
        minWidth: 150
    },
    {
        headerTextId: 'transporterTable.address.pincode',
        accessor: 'address.pincode',
        minWidth: 100
    },
    {
        headerTextId: 'transporterTable.address.city',
        accessor: 'address.city',
        minWidth: 100
    },
    {
        headerTextId: 'transporterTable.address.state',
        accessor: 'address.state',
        minWidth: 100
    },
    {
        headerTextId: 'transporterTable.address.country',
        accessor: 'address.countryCode',
        minWidth: 50
    },
    {
        headerTextId: 'transporterTable.emailAddress',
        accessor: 'emailAddress.emailAddress',
        minWidth: 100
    },
    {
        headerTextId: 'transporterTable.phoneNumber',
        accessor: 'phoneNumber.phoneNumber',
        infixSearchable: true,
        minWidth: 100
    },
    {
        headerTextId: 'transporterTable.additionalPhoneNumbers',
        accessor: 'additionalPhoneNumbers',
        sortable: false,
        filterable: false,
        minWidth: 100,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme }
                }
            } = props;
            const { additionalPhoneNumbers } = (original: MasterTransporter);

            const cellText = additionalPhoneNumbers
                .map(phoneNumber => phoneNumber?.phoneNumber || '')
                .join(', ');

            return (
                <div title={cellText} className={theme.cellText}>
                    {cellText}
                </div>
            );
        }
    },
    {
        headerTextId: 'transporterTable.contactId',
        accessor: 'contactId.contactId',
        minWidth: 100
    },
    {
        headerTextId: 'transporterTable.type',
        accessor: 'type',
        FilterComponent: GeneralFilter,
        getFilterOptions: () => {
            const options = TransporterTypeType.getOptions({
                translationKey: 'masterTransporterProperties.types'
            });
            return options;
        }
    },
    {
        headerTextId: 'transporterTable.teamName',
        id: 'team.name',
        exportId: 'teams.name',
        accessor: 'teams',
        sortable: false,
        FilterComponent: ConnectedTeamSelectorFilter,
        getCustomFilterInfo: getTeamIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const teamIds = (original: MasterTransporter).teams;
            return <TeamList teams={teams} teamIds={teamIds} theme={theme} />;
        }
    },
    {
        headerTextId: 'transporterTable.teamId',
        id: 'team.id',
        exportId: 'teams.id',
        accessor: 'teams',
        sortable: false,
        FilterComponent: ConnectedTeamIdSelectorFilter,
        getCustomFilterInfo: getTeamIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const teamIds = (original: MasterTransporter).teams;
            return (
                <TeamList
                    teams={teams}
                    teamIds={teamIds}
                    theme={theme}
                    selectTeamName={selectTeamId}
                />
            );
        }
    },
    {
        headerTextId: 'transporterTable.logo',
        id: 'logo',
        minWidth: 50,
        sortable: false,
        filterable: false,
        Cell: props => {
            const {
                original: { logo },
                tdProps: {
                    rest: { theme }
                }
            } = props;
            if (logo) {
                return (
                    <div className={theme.cellImage}>
                        <ImageWithTooltip
                            src={logo}
                            className={theme.imageLink}
                            theme={theme}
                        />
                    </div>
                );
            } else {
                return <span>{'-'}</span>;
            }
        }
    },
    {
        headerTextId: 'transporterTable.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original: transporter,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                transporter?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'transporterTable.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'transporterTable.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original: transporter,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                transporter?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'transporterTable.createdOn',
        accessor: 'auditMetadata.createdOn',
        id: 'createdOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    }
];

type PaginationInfoDataType = {
    currentPage: number,
    numberOfPages: number,
    total: number
};

type TransportersDataType = {
    transporters: Array<MasterTransporter>,
    paginationInfo: PaginationInfoDataType
};

type Props = {
    clientId: string,
    theme: Object,
    data: TransportersDataType,
    entitySettings: ?EntitySettings,
    tableSettings: ?Array<Object>,
    t: TFunction,
    skills: Array<Skill>,
    fetchSkills: ({ clientId: string }) => void,
    fetchOptions: () => {}
};

const transporterMasterContainerFactory = ({
    EntityEditViewContainer = SingleTransporterEditContainer,
    EntityImportContainer = TransporterMasterImportContainer,
    COLUMNS = DEFAULT_COLUMNS,
    options = {
        entityTableId: ENTITY_TABLE_ID,
        operations: {
            add: RoleOperationEnum.UPDATE_TRANSPORTER,
            edit: RoleOperationEnum.UPDATE_TRANSPORTER,
            export: RoleOperationEnum.EXPORT_TRANSPORTER
        }
    },
    ActionsBar,
    BulkViewEmptyState,
    fetchEntities = TransporterActions.fetchActiveEntities
}) => {
    const TransporterMaster = entityMasterFactory({
        EntityEditViewContainer,
        EntityImportContainer,
        EntityExportContainer: MasterExportContainerWithDocuments,
        COLUMNS,
        options,
        ActionsBar,
        BulkViewEmptyState
    });
    class TransporterMasterContainer extends PureComponent<Props> {
        componentDidMount = () => {
            this.fetchOtherResources();
        };

        componentDidUpdate = (prevProps: Props) => {
            const { clientId } = this.props;
            if (clientId !== prevProps.clientId) {
                this.fetchOtherResources();
            }
        };

        fetchOtherResources = () => {
            const { clientId, fetchSkills } = this.props;
            fetchSkills({ clientId });
        };

        getTdProps = (state, rowInfo) => {
            if (!rowInfo) {
                return {};
            }
            const {
                original: { id }
            } = rowInfo;
            const {
                t,
                entitySettings,
                selectedRows,
                toggleRowSelection,
                theme,
                teams,
                isGlobalPersonnel,
                skills
            } = this.props;
            const isChecked = selectedRows.indexOf(id) >= 0;
            return {
                onCheckboxClick: () => {
                    toggleRowSelection(id);
                },
                t,
                entitySettings,
                theme,
                isChecked,
                skills,
                isGlobalPersonnel,
                className: classnames({ [theme.selectedRow]: isChecked }),
                teams
            };
        };

        getAdditionalTableProps = () => {
            const { teams } = this.props;
            return {
                teams
            };
        };

        render() {
            const { clientId, fetchOptions } = this.props;
            const transporterFetchOptions = {
                clientId,
                include: 'CONTACT',
                ...(fetchOptions || {})
            };
            return (
                <TransporterMaster
                    {...this.props}
                    emptyStateImageSrc="/img/transporter-empty.svg"
                    entityNameId={ENTITY_NAME_ID}
                    entityExportId={ENTITY_EXPORT_ID}
                    entityId={'transporterId'}
                    entitySettingsName={'transporterSettings'}
                    fetchOptions={transporterFetchOptions}
                    getTdProps={this.getTdProps}
                    getAdditionalTableProps={this.getAdditionalTableProps}
                    enableCascadeWarning
                />
            );
        }
    }

    const mapStateToProps = (state, ownProps) => {
        const clientId = clientSelector(state, ownProps);
        const isLoading = TransporterSelectors.isEntitiesLoading(
            state,
            ownProps
        );
        const data = TransporterSelectors.getEntities(state, ownProps);
        const isAddEntityActive = isAddEntityActiveSelector(state, ownProps);
        const entityIdToEdit = editEntityIdSelector(state, ownProps);
        const entitySettings = getEntitySettings(state, ownProps);
        const tableSettings = getEntityTablePreferences(state, ENTITY_TABLE_ID);
        const teams = teamsSelector(state, ownProps);
        const isImportEntityActive = isImportEntityActiveSelector(
            state,
            ownProps
        );
        const skills = SkillSelectors.getEntities(state, ownProps);
        const isExportEntityActive = isExportEntityActiveSelector(
            state,
            ownProps
        );
        const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);
        return {
            clientId,
            isLoading,
            data,
            isAddEntityActive,
            entityIdToEdit,
            entitySettings,
            tableSettings,
            teams,
            isImportEntityActive,
            skills,
            isExportEntityActive,
            isGlobalPersonnel
        };
    };

    const withRedux = connect(mapStateToProps, {
        fetchEntities,
        showToastAction: ToastActions.showToast,
        updateToastAction: ToastActions.updateToast,
        hideToastAction: ToastActions.hideToast,
        fetchSkills: SkillActions.fetchEntities,
        disableEntity: TransporterActions.disableEntity,
        clearData: TransporterActions.clearData,
        saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
        bulkDisableEntity: TransporterActions.bulkDisableEntity
    });

    const enhance = compose(
        themr(null, theme),
        withRouter,
        withRedux,
        translate('entityManagement'),
        MasterContainerHOC
    );
    return enhance(TransporterMasterContainer);
};

const ThemedConnectedTransporterMasterContainer = transporterMasterContainerFactory(
    {}
);

export {
    transporterMasterContainerFactory,
    ThemedConnectedTransporterMasterContainer as TransporterMasterContainer
};
export default ThemedConnectedTransporterMasterContainer;
